import { supabase } from './supabaseClient.ts';
import { createStripeSession } from './apiService.ts';
import { withSupabaseAvailability } from '../utils/supabaseStatus.ts';

/**
 * Creates a Stripe customer portal session for managing billing
 * @returns Promise<string> - The portal session URL
 */
export async function createStripePortalSession(): Promise<string> {
  return await withSupabaseAvailability(async () => {
    console.log('[billingService.createStripePortalSession] Creating portal session...');
    
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      if (error || !session?.access_token) {
        console.error('[billingService.createStripePortalSession] No active session:', error);
        throw new Error('Authentication required');
      }

      console.log('[billingService.createStripePortalSession] Retrieved access token from current session');
      console.log('[billingService.createStripePortalSession] Token length:', session.access_token.length);
      console.log('[billingService.createStripePortalSession] Token prefix:', session.access_token.substring(0, 20) + '...');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        throw new Error('VITE_SUPABASE_URL not configured');
      }

      const functionUrl = `${supabaseUrl}/functions/v1/create-stripe-portal-session`;
      console.log('[billingService.createStripePortalSession] Calling Edge Function:', functionUrl);

      const makeRequest = async (token: string) => {
        const headers = {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        };

        console.log('[billingService.createStripePortalSession] Request headers prepared, making fetch call...');

        const response = await fetch(functionUrl, {
          method: 'POST',
          headers,
        });

        console.log('[billingService.createStripePortalSession] response status:', response.status);
        console.log('[billingService.createStripePortalSession] response ok:', response.ok);

        return response;
      };

      // First attempt with current token
      let response = await makeRequest(session.access_token);

      // If we get a 401, try refreshing the session once
      if (response.status === 401) {
        console.log('[billingService.createStripePortalSession] Got 401, attempting to refresh session...');
        
        const { data: refreshData, error: refreshError } = await supabase.auth.refreshSession();
        if (refreshError || !refreshData.session?.access_token) {
          console.error('[billingService.createStripePortalSession] Session refresh failed:', refreshError);
          throw new Error('Authentication required');
        }
        
        console.log('[billingService.createStripePortalSession] Session refreshed, retrying request...');
        response = await makeRequest(refreshData.session.access_token);
      }

      if (!response.ok) {
        const errorText = await response.clone().text();
        console.error('[billingService.createStripePortalSession] HTTP error:', response.status, errorText);
        
        // Try to parse the error response for better debugging
        try {
          const errorJson = JSON.parse(errorText);
          console.error('[billingService.createStripePortalSession] Parsed error:', errorJson);
          throw new Error(`Failed to create portal session: ${response.status} - ${errorJson.details || errorJson.error}`);
        } catch (parseError) {
          throw new Error(`Failed to create portal session: ${response.status} - ${errorText}`);
        }
      }

      const result = await response.json();
      console.log('[billingService.createStripePortalSession] Portal session created successfully');
      
      if (!result.url) {
        throw new Error('Portal session URL not returned');
      }

      return result.url;
    } catch (err) {
      console.error('[billingService.createStripePortalSession] Error:', err);
      throw err;
    }
  });
}

/**
 * Creates a Stripe checkout session for upgrading to Pro
 * @returns Promise<string> - The checkout session URL
 */
export async function createProCheckoutSession(): Promise<string> {
  console.log('[billingService.createProCheckoutSession] Creating Pro checkout session...');
  
  try {
    const result = await createStripeSession('pro');
    console.log('[billingService.createProCheckoutSession] Pro checkout session created');
    return result.session_url;
  } catch (err) {
    console.error('[billingService.createProCheckoutSession] Error:', err);
    throw err;
  }
}

/**
 * Creates a Stripe checkout session for one-time scan purchase
 * @returns Promise<string> - The checkout session URL
 */
export async function createOneTimeCheckoutSession(): Promise<string> {
  console.log('[billingService.createOneTimeCheckoutSession] Creating one-time checkout session...');
  
  try {
    const result = await createStripeSession('oneoff');
    console.log('[billingService.createOneTimeCheckoutSession] One-time checkout session created');
    return result.session_url;
  } catch (err) {
    console.error('[billingService.createOneTimeCheckoutSession] Error:', err);
    throw err;
  }
}

/**
 * Utility function to get current billing period info
 * @param subscription - The subscription object
 */
export function getBillingPeriodInfo(subscription: any) {
  if (!subscription?.current_period_end) {
    return null;
  }

  const periodEnd = new Date(subscription.current_period_end);
  const now = new Date();
  const daysRemaining = Math.ceil((periodEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

  return {
    endDate: periodEnd,
    daysRemaining: Math.max(0, daysRemaining),
    isExpired: periodEnd <= now,
    formattedEndDate: periodEnd.toLocaleDateString(),
  };
}

/**
 * Utility function to format subscription status for display
 * @param subscription - The subscription object
 */
export function formatSubscriptionStatus(subscription: any) {
  if (!subscription) {
    return { status: 'None', color: 'gray' };
  }

  const billingInfo = getBillingPeriodInfo(subscription);
  
  if (!subscription.active) {
    return { status: 'Inactive', color: 'red' };
  }

  if (billingInfo?.isExpired) {
    return { status: 'Expired', color: 'red' };
  }

  if (billingInfo && billingInfo.daysRemaining <= 7) {
    return { status: 'Renewing Soon', color: 'orange' };
  }

  return { status: 'Active', color: 'green' };
} 